import React from 'react';
import { Home, Menu } from 'lucide-react';
import { type NavItem } from './NavConfig';

interface BottomNavProps {
  /** The role's home view key ('home', or 'canvass' for a canvasser). */
  homeKey: string;
  /** Up to three role-curated destinations (getBottomNavItems). */
  items: NavItem[];
  currentView: string;
  onNavigate: (viewKey: string) => void;
  /** Opens the hamburger drawer — the full catalog stays one tap away. */
  onOpenMenu: () => void;
}

/**
 * Mobile primary navigation — a fixed, thumb-reachable bottom bar.
 *
 * Five slots max: Home, up to three role-curated tools, Menu. Hidden at lg+
 * where the top bar + drawer remain the navigation. The drawer is secondary
 * nav (the full catalog); this bar is the palm-of-the-hand path to the tools
 * a role actually opens every day.
 *
 * AppShell reserves space for it under <main> (fixed-element-offset) and the
 * bar itself pads for the home-indicator safe area.
 */
export const BottomNav: React.FC<BottomNavProps> = ({
  homeKey,
  items,
  currentView,
  onNavigate,
  onOpenMenu,
}) => {
  const slots: Array<{
    key: string;
    label: string;
    Icon: NavItem['Icon'];
    onPress: () => void;
    active: boolean;
  }> = [
    {
      key: homeKey,
      label: 'Home',
      Icon: Home,
      onPress: () => onNavigate(homeKey),
      active: currentView === homeKey,
    },
    ...items.map((item) => ({
      key: item.key,
      label: item.label,
      Icon: item.Icon,
      onPress: () => onNavigate(item.key),
      active: currentView === item.key,
    })),
  ];

  return (
    <nav
      aria-label="Primary"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-bg-base/95 backdrop-blur border-t border-border-base"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-stretch max-w-7xl mx-auto">
        {slots.map(({ key, label, Icon, onPress, active }) => (
          <button
            key={key}
            onClick={onPress}
            aria-current={active ? 'page' : undefined}
            className={`flex-1 min-w-0 min-h-14 py-1.5 flex flex-col items-center justify-center gap-0.5 transition-colors [touch-action:manipulation] ${
              active
                ? 'text-accent-fg'
                : 'text-fg-muted hover:text-fg-secondary active:text-fg-primary'
            }`}
          >
            {/* Active indicator: a token-colored bar, not color alone. */}
            <span
              aria-hidden="true"
              className={`block h-0.5 w-8 rounded-full mb-0.5 ${active ? 'bg-accent' : 'bg-transparent'}`}
            />
            <Icon className="w-5 h-5" aria-hidden="true" />
            <span className="text-[11px] font-medium leading-tight truncate max-w-full px-1">
              {label}
            </span>
          </button>
        ))}
        <button
          onClick={onOpenMenu}
          className="flex-1 min-w-0 min-h-14 py-1.5 flex flex-col items-center justify-center gap-0.5 text-fg-muted hover:text-fg-secondary active:text-fg-primary transition-colors [touch-action:manipulation]"
          aria-label="Open full navigation menu"
        >
          <span aria-hidden="true" className="block h-0.5 w-8 rounded-full mb-0.5 bg-transparent" />
          <Menu className="w-5 h-5" aria-hidden="true" />
          <span className="text-[11px] font-medium leading-tight">Menu</span>
        </button>
      </div>
    </nav>
  );
};
